import { useState, useRef, useEffect } from 'react';

interface BarChartProps {
  data: { label: string; value: number; color?: string }[];
  height?: number;
  currency?: string;
  showValues?: boolean;
}

export default function BarChart({ data, height = 200, currency = '', showValues = true }: BarChartProps) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(400);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    observer.observe(containerRef.current);
    setContainerWidth(containerRef.current.clientWidth);
    return () => observer.disconnect();
  }, []);

  const maxValue = Math.max(...data.map(d => d.value), 1);
  const yAxisWidth = 44;
  const labelAreaHeight = 50;
  const topPadding = 24;
  const rightPadding = 10;
  const availableWidth = containerWidth - yAxisWidth - rightPadding;
  const gap = 8;
  const barWidth = Math.max(16, Math.min(48, (availableWidth / data.length) - gap));
  const totalBarsWidth = data.length * (barWidth + gap);
  const needsScroll = totalBarsWidth > availableWidth;
  const svgWidth = needsScroll ? totalBarsWidth + yAxisWidth + rightPadding : containerWidth;
  const svgHeight = height + labelAreaHeight + topPadding;

  const colors = [
    '#0d9488', '#0ea5e9', '#f59e0b', '#ef4444', '#8b5cf6',
    '#ec4899', '#14b8a6', '#6366f1', '#f97316', '#06b6d4',
  ];

  return (
    <div ref={containerRef} className="w-full overflow-x-auto">
      <svg
        width={svgWidth}
        height={svgHeight}
        viewBox={`0 0 ${svgWidth} ${svgHeight}`}
      >
        {[0, 0.25, 0.5, 0.75, 1].map((fraction) => {
          const y = topPadding + height - fraction * height;
          const value = fraction * maxValue;
          return (
            <g key={fraction}>
              <line x1={yAxisWidth} y1={y} x2={svgWidth - rightPadding} y2={y} stroke="rgba(255,255,255,0.1)" strokeWidth="1" strokeDasharray={fraction === 0 ? '0' : '4'} />
              <text x={yAxisWidth - 4} y={y + 4} textAnchor="end" fill="rgba(255,255,255,0.7)" fontSize="10">
                {value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value.toFixed(0)}
              </text>
            </g>
          );
        })}

        {data.map((item, i) => {
          const barHeight = (item.value / maxValue) * height;
          const x = yAxisWidth + 6 + i * (barWidth + gap);
          const y = topPadding + height - barHeight;
          const color = item.color || colors[i % colors.length];
          const isHovered = hoveredIndex === i;
          const tooltipY = Math.max(12, y - 6);

          return (
            <g
              key={i}
              onMouseEnter={() => setHoveredIndex(i)}
              onMouseLeave={() => setHoveredIndex(null)}
              style={{ cursor: 'pointer' }}
            >
              <rect
                x={x}
                y={y}
                width={barWidth}
                height={barHeight}
                rx={4}
                fill={color}
                opacity={isHovered ? 1 : 0.85}
                style={{ transition: 'all 0.2s ease' }}
              />
              {showValues && isHovered && (
                <>
                  <rect
                    x={x + barWidth / 2 - 28}
                    y={tooltipY - 12}
                    width={56}
                    height={18}
                    rx={4}
                    fill="#1e293b"
                  />
                  <text x={x + barWidth / 2} y={tooltipY} textAnchor="middle" fill="#ffffff" fontSize="10" fontWeight="600">
                    {currency}{item.value.toFixed(2)}
                  </text>
                </>
              )}
              <foreignObject
                x={x - (gap / 2)}
                y={topPadding + height + 6}
                width={barWidth + gap}
                height={labelAreaHeight - 6}
              >
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    overflow: 'hidden',
                  }}
                >
                  <span
                    style={{
                      fontSize: '10px',
                      fill: 'rgba(255,255,255,0.7)',
                      color: 'rgba(255,255,255,0.7)',
                      textAlign: 'center',
                      lineHeight: '1.2',
                      wordBreak: 'break-word',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical' as any,
                      overflow: 'hidden',
                    }}
                  >
                    {item.label}
                  </span>
                </div>
              </foreignObject>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
